import React, { useEffect, useState } from 'react';

/**
 * **설정 탭**(사용자 2026-10-13) — 길마·부길마(와 사이트 운영자)만 보인다. 카톡 봇 설정과 [설탕봇 설치] 내려받기(2026-10-30).
 *
 *   봇 동작 ON/OFF        끄면 알림도 명령어 답도 없다
 *   단톡방 / 운영자방     알림 종류마다 어느 방에 보낼지
 *
 * 바꾸면 바로 서버에 저장한다(`PUT /api/kakao-settings`). 봇은 20초마다 서버에 묻고 서버가 정한 방으로만 보내므로,
 * 봇을 다시 켜거나 컴파일할 필요가 없다.
 */
type RoomKey = 'guild' | 'ops' | 'notice';
type AlertKind = 'fail' | 'hold' | 'event' | 'coupon' | 'notice' | 'board' | 'siegeStart' | 'siegeEnd';
interface KakaoConfig {
  enabled: boolean;
  rooms: Record<RoomKey, Record<AlertKind, boolean>>;
}

const ROOMS: { key: RoomKey; label: string }[] = [
  { key: 'guild', label: '단톡방' },
  { key: 'ops', label: '운영자방' },
  // 사이트 공지를 받는 방(사용자 2026-10-27) — 봇의 NOTICE_CHAT
  { key: 'notice', label: '공지방' }
];

/** 이벤트·공지는 공식 카페 게시판, 쿠폰은 swgt.io/gamecodes — 서버가 5분마다 본다(`utils/kakaoFeeds.ts`) */
const ALERTS: { key: AlertKind; label: string }[] = [
  // 사이트 [공지] 탭에 새 공지가 올라오면 — 알람 중 맨 위(사용자 2026-10-27)
  { key: 'board', label: '사이트 공지 알람' },
  // 점령전이 열리거나 끝나면 글 + 카드 그림(사용자 2026-10-29)
  { key: 'siegeStart', label: '점령전 개전 알람' },
  { key: 'siegeEnd', label: '점령전 종료 알람' },
  { key: 'fail', label: '공실 알람' },
  { key: 'hold', label: '방성 알람' },
  { key: 'event', label: '이벤트 알람' },
  { key: 'notice', label: '공지 알람' },
  { key: 'coupon', label: '쿠폰 알람' }
];

/** 다른 탭(휴가 등)과 같은 색 — 보라 그라데이션 바탕 위에 흰 카드 */
const PURPLE = '#667eea';

/** [설탕봇 설치] — 서버 `backend/downloads/` 에 올려 둔 것(`GET /api/downloads`) */
interface DownloadFile { name: string; label: string; size: number; updatedAt: string }
const sizeText = (b: number) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(1)}GB` : `${Math.round(b / 1024 ** 2)}MB`);
const dateText = (iso: string) => { const d = new Date(iso); return `${d.getMonth() + 1}/${d.getDate()}`; };

const Toggle: React.FC<{ on: boolean; disabled?: boolean; onChange: (v: boolean) => void; label: string }> = ({ on, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!on)}
    style={{
      width: 46, height: 26, borderRadius: 13, border: 'none', padding: 0, cursor: disabled ? 'default' : 'pointer',
      background: on ? PURPLE : '#cfd2dc', position: 'relative', transition: 'background 0.15s', opacity: disabled ? 0.45 : 1,
      flexShrink: 0
    }}
  >
    <span style={{
      position: 'absolute', top: 3, left: on ? 23 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'left 0.15s'
    }} />
  </button>
);

const SettingsPage: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const [cfg, setCfg] = useState<KakaoConfig | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState<DownloadFile[] | null>(null);

  useEffect(() => {
    fetch('/api/downloads', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setFiles(d?.success ? d.files : []))
      .catch(() => setFiles([]));
  }, []);

  useEffect(() => {
    fetch('/api/kakao-settings', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => (d?.success ? setCfg(d.config) : setError(d?.error || '설정을 불러오지 못했습니다')))
      .catch(() => setError('설정을 불러오지 못했습니다'));
  }, []);

  const save = (next: KakaoConfig) => {
    const prev = cfg;
    setCfg(next);
    setSaving(true);
    setError('');
    fetch('/api/kakao-settings', {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ config: next })
    })
      .then((r) => r.json())
      .then((d) => {
        if (d?.success) setCfg(d.config);
        else { setCfg(prev); setError(d?.error || '저장하지 못했습니다'); }
      })
      .catch(() => { setCfg(prev); setError('저장하지 못했습니다'); })
      .finally(() => setSaving(false));
  };

  // 다른 탭과 같은 짜임(사용자 2026-10-13): 보라 그라데이션 바탕 · 흰 반투명 카드(둥글기 20, 그림자) · 가운데 제목
  const pad = isMobile ? 'clamp(10px, 2.5vw, 14px)' : 20;
  const card: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.95)', borderRadius: 20, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    padding: pad, marginBottom: 16, textAlign: 'left'   // 탭 틀이 가운데 정렬이라 카드 안은 왼쪽으로(사용자)
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: isMobile ? 'clamp(12px, 3vw, 20px) clamp(6px, 2vw, 12px)' : '32px 20px'
    }}>
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      {/* 머리말 */}
      <div style={card}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 'clamp(18px, 4vw, 22px)' : 26, fontWeight: 800, color: PURPLE, textAlign: 'center' }}>설정</h2>
        <p style={{ margin: '6px 0 0', fontSize: isMobile ? 'clamp(11px, 2.4vw, 12px)' : 14, color: '#666', textAlign: 'center', fontWeight: 500 }}>
          길마·부길마만 보이는 화면입니다
        </p>
      </div>

      {error && <div style={{ ...card, color: '#d32f2f', fontSize: 14, textAlign: 'center' }}>{error}</div>}
      {!cfg && !error && <div style={{ ...card, color: '#888', fontSize: 14, textAlign: 'center' }}>불러오는 중…</div>}

      {cfg && (
        <>
          <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: PURPLE }}>설탕봇 전원</div>
            </div>
            <Toggle label="설탕봇 전원" on={cfg.enabled} disabled={saving} onChange={(v) => save({ ...cfg, enabled: v })} />
          </div>

          <div style={{ ...card, opacity: cfg.enabled ? 1 : 0.55 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: PURPLE, marginBottom: 10 }}>알림 보낼 방</div>
            <div style={{ display: 'grid', gridTemplateColumns: `minmax(0, 1fr) repeat(${ROOMS.length}, ${isMobile ? 58 : 88}px)`, alignItems: 'center', rowGap: 12 }}>
              <div />
              {ROOMS.map((r) => (
                <div key={r.key} style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#555' }}>{r.label}</div>
              ))}
              {ALERTS.map((a) => (
                <React.Fragment key={a.key}>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: '#333' }}>
                    {a.label}
                  </div>
                  {ROOMS.map((r) => (
                    <div key={r.key} style={{ display: 'flex', justifyContent: 'center' }}>
                      <Toggle
                        label={`${r.label} ${a.label}`}
                        on={cfg.rooms[r.key][a.key]}
                        disabled={saving || !cfg.enabled}
                        onChange={(v) => save({ ...cfg, rooms: { ...cfg.rooms, [r.key]: { ...cfg.rooms[r.key], [a.key]: v } } })}
                      />
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </>
      )}

      {/* 설탕봇 설치 — 다른 PC 에서 수집·자동 조작을 돌릴 때(사용자 2026-10-30 "에이전트와 ldplayer 저장소 받을 수 있게") */}
      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 800, color: PURPLE, marginBottom: 10 }}>설탕봇 설치</div>
        {files === null && <div style={{ fontSize: 13, color: '#888' }}>불러오는 중…</div>}
        {files && !files.length && <div style={{ fontSize: 13, color: '#888' }}>아직 올라온 파일이 없습니다</div>}
        {files && files.map((f) => (
          <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid rgba(102, 126, 234, 0.12)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: '#333' }}>{f.label}</div>
              <div style={{ fontSize: 12, color: '#888', fontWeight: 600 }}>{f.name} · {sizeText(f.size)} · {dateText(f.updatedAt)}</div>
            </div>
            <a href={`/api/downloads/${encodeURIComponent(f.name)}`} download style={{
              flexShrink: 0, textDecoration: 'none', border: `1.5px solid ${PURPLE}`, color: PURPLE, borderRadius: 10,
              padding: '6px 14px', fontSize: 13, fontWeight: 800
            }}>받기</a>
          </div>
        ))}
        <div style={{ marginTop: 8, fontSize: 12.5, color: '#666', lineHeight: 1.6, fontWeight: 500 }}>
          LDPlayer 14 설치 → 설치 파일 압축 풀기 → LDPlayer 백업을 setup.cmd 옆에 두기 → setup.cmd 실행 → 게임 로그인
        </div>
      </div>
    </div>
    </div>
  );
};

export default SettingsPage;
